import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { detectPrfSupport, type PrfSupportResult } from '@cef-ai/wallet-identity';

export interface BrowserSupportGateProps {
  children: ReactNode;
}

export function BrowserSupportGate({ children }: BrowserSupportGateProps) {
  const [diag, setDiag] = useState<PrfSupportResult | null>(null);
  const [done, setDone] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Test seam: `VITE_SKIP_BROWSER_CHECK=true` shortcuts the WebAuthn/PRF
    // probe and reports "supported" so Playwright (headless Chromium has no
    // platform authenticator by default) can drive route smokes. Cycle 8 will
    // replace this with a Playwright virtual authenticator.
    //
    // WARNING: do NOT set this env var in production builds — it will let
    // unsupported browsers reach the register/sign flows and fail at WebAuthn
    // call time with a less friendly error.
    if ((import.meta as any).env?.VITE_SKIP_BROWSER_CHECK === 'true') {
      setDiag({ webAuthn: true, platformAuthenticator: true, prfPotentiallySupported: true });
      setDone(true);
      return;
    }
    let cancelled = false;
    detectPrfSupport()
      .then((res) => {
        if (cancelled) return;
        setDiag(res);
        setDone(true);
      })
      .catch(() => {
        if (cancelled) return;
        setDiag(null);
        setDone(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!done) return;
    if (location.pathname === '/not-supported') return;
    // Block only on a definitive negative: no WebAuthn at all, or the browser
    // explicitly reported no PRF support. A platform authenticator is NOT
    // required — security keys and phone passkeys are valid. Anything else
    // proceeds; the ceremony's prf-unsupported error is the final safety net.
    if (!diag || !diag.webAuthn || !diag.prfPotentiallySupported) {
      navigate('/not-supported', { replace: true });
    }
  }, [done, diag, location.pathname, navigate]);

  if (!done) {
    return (
      <main style={{ padding: '2rem', textAlign: 'center', fontFamily: 'system-ui' }}>
        Checking browser compatibility…
      </main>
    );
  }
  return <>{children}</>;
}
